import { Button, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconBrain, IconBuilding, IconEyeOff, IconLockHeart, IconShieldCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { axioTokens } from "../theme";

const STORAGE_KEY = "axiohire.candidate-pool-promise.dismissed";

const PROMISES = [
  { icon: IconBrain, title: "AI stays out of your bench", body: "We never train models or improve the product using your candidates." },
  { icon: IconEyeOff, title: "No public marketplace", body: "Other recruiters cannot discover, browse, or extract your pool." },
  { icon: IconBuilding, title: "You control company visibility", body: "A company sees a profile only after you submit that person to its job." },
  { icon: IconLockHeart, title: "Ownership does not expire", body: "Leave AxioHire tomorrow and your bench is never repurposed as marketplace inventory." },
];

export function CandidatePoolPromise() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore quota / private mode */
    }
    setDismissed(true);
  }

  return (
    <Paper
      radius={axioTokens.radius.md}
      p="xl"
      pos="relative"
      style={{ background: axioTokens.surfaceElevated, borderColor: axioTokens.border }}
    >
      <Button
        variant="default"
        color="gray"
        size="xs"
        aria-label="Dismiss"
        rightSection={<IconX size="var(--axio-unit-14)" />}
        onClick={dismiss}
        style={{
          position: "absolute",
          top: axioTokens.spacing.md,
          right: axioTokens.spacing.md,
          zIndex: axioTokens.number.one,
        }}
      >
        Cancel
      </Button>
      <Stack gap="xl">
        <Stack gap="sm" pr="xl">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon radius={axioTokens.radius.md} size="lg" variant="light" color="violet">
              <IconShieldCheck size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-strong)" />
            </ThemeIcon>
            <Text size="xs" tt="uppercase" fw="var(--axio-font-weight-bold)" c="violet">
              Candidate privacy promise
            </Text>
          </Group>
          <Title order={2}>Your candidate pool stays yours. Forever.</Title>
          <Text c="dimmed" maw="var(--axio-unit-640)">
            Your bench is an asset you built. We do not reuse it as marketplace inventory.
          </Text>
        </Stack>
        <SimpleGrid type="container" cols={{ base: 1, [axioTokens.dimension.containerPromise]: 2 }} spacing="lg">
          {PROMISES.map(({ icon: Icon, title, body }) => (
            <Group key={title} align="flex-start" wrap="nowrap" gap="sm">
              <ThemeIcon variant="light" radius={axioTokens.radius.md} size="md" color="violet">
                <Icon size="var(--axio-unit-18)" stroke="var(--axio-stroke-width-regular)" />
              </ThemeIcon>
              <Stack gap="var(--axio-unit-4)">
                <Text fw="var(--axio-font-weight-bold)" size="sm">
                  {title}
                </Text>
                <Text c="dimmed" size="sm">
                  {body}
                </Text>
              </Stack>
            </Group>
          ))}
        </SimpleGrid>
        <Group justify="space-between" align="center" wrap="wrap" gap="md" pt="md" style={{ borderTop: `${axioTokens.unit[1]}px solid ${axioTokens.border}` }}>
          <Text size="xs" c="dimmed">
            You can dismiss this note—it will not return on later visits.
          </Text>
          <Button variant="subtle" size="xs" color="gray" onClick={dismiss}>
            Got it
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
